import {
    Box,
    Button,
    Checkbox,
    CheckboxGroup,
    FormControl,
    FormLabel,
    HStack,
    Image,
    Input,
    NumberDecrementStepper,
    NumberIncrementStepper,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    Select,
    SimpleGrid,
    Switch,
    Text,
    Textarea,
    useColorModeValue,
    useToast,
    VStack,
} from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';
import FormModal from '../../../components/FormModal';
import FormStack from '../../../components/form-stack';
import { translate } from '../../../i18n';
import { Currency } from '../../../models/court-pricing/dto/court-pricing-rule.dto';
import { SportType } from '../../../models/court/dto/court.dto';
import { ProductCategory, ProductDto, UpdateProductDto } from '../../../models/product/dto/product.dto';
import { useAvailableLocations } from '../../location-management/hooks/useAvailableLocations';
import { useIsSingleClub } from '../../location-management/hooks/useSingleClubAutoSelect';
import { UpdateProductWithImageInput, useUpdateProduct } from '../hooks/useUpdateProduct';
import { parsePrice } from '../../../utils/parse-price';

interface EditProductModalProps {
    isOpen: boolean;
    onClose: () => void;
    product: ProductDto;
}

export default function EditProductModal({ isOpen, onClose, product }: EditProductModalProps) {
    const { data: availableLocations } = useAvailableLocations();
    const updateProductMutation = useUpdateProduct(product.id);
    const toast = useToast();
    const imageBorderColor = useColorModeValue('gray.200', 'whiteAlpha.300');
    const subtleTextColor = useColorModeValue('gray.600', 'gray.400');

    // MYS-66: hide the club selector when the org has a single club (the
    // product already carries its locationId, so nothing to auto-select).
    const { isSingleClub } = useIsSingleClub();

    const [formData, setFormData] = useState<Omit<UpdateProductDto, 'imageUrl'>>({
        name: product.name,
        description: product.description || '',
        sports: product.sports || [],
        price: product.price,
        currency: product.currency,
        stockQuantity: product.stockQuantity,
        hasUnlimitedStock: product.hasUnlimitedStock,
        category: product.category,
        locationId: product.locationId,
        isActive: product.isActive,
    });

    const [isUnlimitedStock, setIsUnlimitedStock] = useState(product.hasUnlimitedStock);
    const [selectedImage, setSelectedImage] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setFormData({
            name: product.name,
            description: product.description || '',
            sports: product.sports || [],
            price: product.price,
            currency: product.currency,
            stockQuantity: product.stockQuantity,
            hasUnlimitedStock: product.hasUnlimitedStock,
            category: product.category,
            locationId: product.locationId,
            isActive: product.isActive,
        });
        setIsUnlimitedStock(product.hasUnlimitedStock);
        setSelectedImage(null);
        setImagePreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, [product]);

    const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedImage(file);

            // Create preview
            const reader = new FileReader();
            reader.onload = (e) => {
                setImagePreview(e.target?.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveImage = () => {
        setSelectedImage(null);
        setImagePreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleSubmit = async () => {
        const submitData: UpdateProductWithImageInput = {
            productData: {
                ...formData,
                hasUnlimitedStock: isUnlimitedStock,
            },
            imageFile: selectedImage || undefined,
        };

        try {
            await updateProductMutation.mutateAsync(submitData);
            toast({
                title: translate('product.update_success') || 'Product updated successfully',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            onClose();
        } catch (error) {
            toast({
                title: translate('product.update_error') || 'Error updating product',
                description: error instanceof Error ? error.message : (translate('product.unknown_error') || 'Unknown error'),
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const handleSportsChange = (selectedSports: string[]) => {
        setFormData(prev => ({
            ...prev,
            sports: selectedSports as SportType[]
        }));
    };

    return (
        <FormModal
            isOpen={isOpen}
            onClose={onClose}
            title={translate('product.edit') || 'Edit Product'}
            onSubmit={handleSubmit}
            submitLabel={translate('product.save') || 'Save'}
            cancelLabel={translate('product.cancel') || 'Cancel'}
            isSubmitting={updateProductMutation.isLoading}
            size="2xl"
        >
                        <FormStack density="section">
                            <FormControl isRequired>
                                <FormLabel>{translate('product.name') || 'Name'}</FormLabel>
                                <Input
                                    value={formData.name}
                                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder={translate('product.name_placeholder') || 'Enter product name'}
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel>{translate('product.description') || 'Description'}</FormLabel>
                                <Textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                    placeholder={translate('product.description_placeholder') || 'Enter product description'}
                                    rows={3}
                                />
                            </FormControl>

                            <SimpleGrid columns={2} spacing={4} width="100%">
                                <FormControl isRequired>
                                    <FormLabel>{translate('product.category') || 'Category'}</FormLabel>
                                    <Select
                                        value={formData.category}
                                        onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value as ProductCategory }))}
                                    >
                                        {Object.values(ProductCategory).map((category) => (
                                            <option key={category} value={category}>
                                                {translate(`product.categories.${category}` as any) || category}
                                            </option>
                                        ))}
                                    </Select>
                                </FormControl>

                                {!isSingleClub && (
                                    <FormControl isRequired>
                                        <FormLabel>{translate('product.location') || 'Location'}</FormLabel>
                                        <Select
                                            value={formData.locationId}
                                            onChange={(e) => setFormData(prev => ({ ...prev, locationId: e.target.value }))}
                                        >
                                            {availableLocations?.map((location) => (
                                                <option key={location.id} value={location.id}>
                                                    {location.name}
                                                </option>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            </SimpleGrid>

                            <SimpleGrid columns={2} spacing={4} width="100%">
                                <FormControl isRequired>
                                    <FormLabel>{translate('product.price') || 'Price'}</FormLabel>
                                    <NumberInput
                                        value={formData.price}
                                        onChange={(valueString) =>
                                            setFormData(prev => ({ ...prev, price: parsePrice(valueString) || 0 }))
                                        }
                                        min={0}
                                        precision={2}
                                    >
                                        <NumberInputField />
                                        <NumberInputStepper>
                                            <NumberIncrementStepper />
                                            <NumberDecrementStepper />
                                        </NumberInputStepper>
                                    </NumberInput>
                                </FormControl>

                                <FormControl isRequired>
                                    <FormLabel>{translate('product.currency') || 'Currency'}</FormLabel>
                                    <Select
                                        value={formData.currency}
                                        onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value as Currency }))}
                                    >
                                        {Object.values(Currency).map((currency) => (
                                            <option key={currency} value={currency}>
                                                {currency.toUpperCase()}
                                            </option>
                                        ))}
                                    </Select>
                                </FormControl>
                            </SimpleGrid>

                            <FormControl>
                                <FormLabel>{translate('product.stock') || 'Stock'}</FormLabel>
                                <VStack align="start" spacing={2}>
                                    <HStack>
                                        <Switch
                                            isChecked={isUnlimitedStock}
                                            onChange={(e) => setIsUnlimitedStock(e.target.checked)}
                                        />
                                        <Text fontSize="sm">{translate('product.unlimited_stock') || 'Unlimited stock'}</Text>
                                    </HStack>
                                    {!isUnlimitedStock && (
                                        <NumberInput
                                            value={formData.stockQuantity}
                                            onChange={(valueString, valueNumber) =>
                                                setFormData(prev => ({ ...prev, stockQuantity: valueNumber || 0 }))
                                            }
                                            min={0}
                                            width="100%"
                                        >
                                            <NumberInputField />
                                            <NumberInputStepper>
                                                <NumberIncrementStepper />
                                                <NumberDecrementStepper />
                                            </NumberInputStepper>
                                        </NumberInput>
                                    )}
                                </VStack>
                            </FormControl>

                            <FormControl>
                                <FormLabel>{translate('product.status') || 'Status'}</FormLabel>
                                <HStack>
                                    <Switch
                                        isChecked={formData.isActive}
                                        onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                                    />
                                    <Text fontSize="sm">
                                        {formData.isActive ? (translate('product.status_active') || 'Active') : (translate('product.status_inactive') || 'Inactive')}
                                    </Text>
                                </HStack>
                            </FormControl>

                            <FormControl>
                                <FormLabel>{translate('product.sports') || 'Associated Sports'}</FormLabel>
                                <CheckboxGroup
                                    value={formData.sports}
                                    onChange={handleSportsChange}
                                >
                                    <SimpleGrid columns={2} spacing={2}>
                                        {Object.values(SportType).map((sport) => (
                                            <Checkbox key={sport} value={sport}>
                                                {sport}
                                            </Checkbox>
                                        ))}
                                    </SimpleGrid>
                                </CheckboxGroup>
                            </FormControl>

                            <FormControl>
                                <FormLabel>{translate('product.image') || 'Product Image'}</FormLabel>
                                <VStack align="start" spacing={3}>
                                    <Input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageChange}
                                        display="none"
                                    />
                                    <HStack>
                                        <Button
                                            onClick={() => fileInputRef.current?.click()}
                                            variant="outline"
                                            size="sm"
                                        >
                                            {translate('product.select_new_image') || 'Select New Image'}
                                        </Button>
                                        {selectedImage && (
                                            <Button
                                                onClick={handleRemoveImage}
                                                variant="ghost"
                                                size="sm"
                                                colorScheme="red"
                                            >
                                                {translate('product.remove') || 'Remove'}
                                            </Button>
                                        )}
                                    </HStack>

                                    {/* Show current image if exists and no new image selected */}
                                    {product.imageUrl && !imagePreview && (
                                        <Box>
                                            <Text fontSize="sm" mb={2}>
                                                {translate('product.current_image') || 'Current Image:'}
                                            </Text>
                                            <Image
                                                src={product.imageUrl}
                                                alt="Current product image"
                                                maxW="200px"
                                                maxH="200px"
                                                objectFit="cover"
                                                borderRadius="md"
                                                border="1px solid"
                                                borderColor={imageBorderColor}
                                            />
                                        </Box>
                                    )}

                                    {/* Show preview of new image */}
                                    {imagePreview && (
                                        <Box>
                                            <Text fontSize="sm" mb={2}>
                                                {translate('product.new_image_preview') || 'New Image Preview:'}
                                            </Text>
                                            <Image
                                                src={imagePreview}
                                                alt="New product preview"
                                                maxW="200px"
                                                maxH="200px"
                                                objectFit="cover"
                                                borderRadius="md"
                                                border="1px solid"
                                                borderColor={imageBorderColor}
                                            />
                                        </Box>
                                    )}

                                    {selectedImage && (
                                        <Text fontSize="sm" color={subtleTextColor}>
                                            {translate('product.selected') || 'Selected:'} {selectedImage.name}
                                        </Text>
                                    )}
                                </VStack>
                            </FormControl>
                        </FormStack>
        </FormModal>
    );
}
